import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../index';
import {
  WidgetConfigStateModel,
  WidgetConfigPayload,
  SetWidgetOptionsPayload,
  WidgetOptionsWithKey,
  UpdateWidgetOptionPayload,
  AddWidgetOptionsPayload,
} from '../../models/widget-config.model';

// identifier
export const COMPOSER_SLICE_ID = 'widgetConfig';

// default state
const initialState: WidgetConfigStateModel = {
  widgetConfig: {},
  widgetOptions: {},
};

//slice
const composerSlice = createSlice({
  name: COMPOSER_SLICE_ID,
  initialState,
  reducers: {
    setWidgetConfig: (
      state: WidgetConfigStateModel,
      action: PayloadAction<WidgetConfigPayload>
    ): void => {
      if (!state.widgetConfig[action.payload.widgetId]) {
        state.widgetConfig[action.payload.widgetId] = {};
      }
      state.widgetConfig[action.payload.widgetId] = {
        ...state.widgetConfig[action.payload.widgetId],
        ...action.payload.widgetConfig,
      };
    },
    setWidgetOptions: (
      state: WidgetConfigStateModel,
      action: PayloadAction<SetWidgetOptionsPayload>
    ): void => {
      state.widgetOptions[action.payload.projectId] = action.payload.widgetOptions;
    },
    addWidgetOptions: (
      state: WidgetConfigStateModel,
      action: PayloadAction<AddWidgetOptionsPayload>
    ): void => {
      state.widgetOptions[action.payload.projectId] = [
        ...state.widgetOptions[action.payload.projectId],
        action.payload.widgetOptions,
      ];
    },
    updateWidgetOption: (
      state: WidgetConfigStateModel,
      action: PayloadAction<UpdateWidgetOptionPayload>
    ): void => {
      state.widgetOptions[action.payload.projectId] = state.widgetOptions[
        action.payload.projectId
      ].map((option) => {
        if (option.key === action.payload.widgetOptionsWithKey.key) {
          return action.payload.widgetOptionsWithKey;
        } else {
          return option;
        }
      });
    },
  },
});

//selectors
export const selectWidgetConfig = (state: RootState) => state.widgetConfig.widgetConfig;
export const selectWidgetConfigById = (state: RootState, widgetId: string) =>
  state.widgetConfig.widgetConfig[widgetId];
export const selectWidgetOptions = createSelector(
  [
    (state: RootState) => state.widgetConfig.widgetOptions,
    (state: RootState, projectId: string) => projectId,
  ],
  (widgetOptions, projectId): WidgetOptionsWithKey[] => widgetOptions[projectId]
);

export const selectAllWidgetOptionKeys = createSelector(
  [
    (state: RootState) => state.widgetConfig.widgetOptions,
    (state: RootState, projectId: string) => projectId,
  ],
  (widgetOptions, projectId): number[] =>
    widgetOptions[projectId]?.map((option) => option.key) || []
);

export const selectWidgetOptionsById = (state: RootState, projectId: string, widgetId: string) =>
  state.widgetConfig.widgetOptions[projectId]?.find((option) => option.value.id === widgetId);

//actions
export const { setWidgetConfig, setWidgetOptions, addWidgetOptions, updateWidgetOption } =
  composerSlice.actions;

//reducer
export default composerSlice;
